import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Slider } from "@/components/ui/slider";
import {
  ChartLineUp, ShieldCheck, Storefront, Sparkle, Info, CaretDown, CaretUp, Spinner,
} from "@phosphor-icons/react";

const money = (v) => `$${Number(Math.round(v || 0)).toLocaleString()}`;

const CONF = {
  High: { cls: "bg-[#DCFCE7] text-[#166534] border-[#86EFAC]", label: "High confidence" },
  Medium: { cls: "bg-[#FEF3C7] text-[#92400E] border-[#FCD34D]", label: "Medium confidence" },
  Low: { cls: "bg-[#F1EFE9] text-[#57534E] border-[#D6D3CD]", label: "Low confidence" },
};

const STRATS = [
  { key: "quick_sale", title: "Quick sale", sub: "Priced to move — expect a faster sale." },
  { key: "balanced", title: "Balanced", sub: "Market median — a fair, typical price." },
  { key: "max_return", title: "Max return", sub: "Top of the range — may take longer to sell." },
];

// Interpolate a target price from the strategy slider (0=quick, 50=balanced, 100=max).
function strategyPrice(anchors, pos) {
  if (!anchors) return 0;
  const { quick_sale: q, balanced: b, max_return: m } = anchors;
  if (pos <= 50) return q + ((b - q) * pos) / 50;
  return b + ((m - b) * (pos - 50)) / 50;
}

export default function PricingPanel({
  itemType = "", brandDesigner = "", periodEra = "", itemCategory = "", condition = "",
  onUse, variant = "page",
}) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pos, setPos] = useState(50);
  const [rationale, setRationale] = useState("");
  const [explaining, setExplaining] = useState(false);
  const [showSources, setShowSources] = useState(variant === "page");

  useEffect(() => {
    let active = true;
    setLoading(true);
    setRationale("");
    const t = setTimeout(() => {
      const params = new URLSearchParams({
        item_type: itemType || "", brand_designer: brandDesigner || "",
        period_era: periodEra || "", item_category: itemCategory || "", condition: condition || "",
      });
      api.get(`/pricing-analysis?${params.toString()}`)
        .then(({ data }) => { if (active) setData(data); })
        .catch(() => { if (active) setData(null); })
        .finally(() => { if (active) setLoading(false); });
    }, 350);
    return () => { active = false; clearTimeout(t); };
  }, [itemType, brandDesigner, periodEra, itemCategory, condition]);

  const rec = data?.recommendation;
  const sold = data?.recent_sales;
  const target = useMemo(() => strategyPrice(rec?.anchors, pos), [rec, pos]);
  const strat = pos <= 33 ? STRATS[0] : pos >= 67 ? STRATS[2] : STRATS[1];

  const explain = async () => {
    if (!rec?.available) return;
    setExplaining(true);
    try {
      const { data: r } = await api.post("/pricing-analysis/explain", {
        item_type: itemType, brand_designer: brandDesigner, period_era: periodEra,
        item_category: itemCategory, condition,
        low: rec.low, point: rec.point, high: rec.high, confidence: rec.confidence,
        scope: rec.scope, sample_size: sold?.sample_size || 0,
        avg_days_to_sale: sold?.avg_days_to_sale ?? null,
      });
      setRationale(r.rationale || "");
    } catch { setRationale(""); } finally { setExplaining(false); }
  };

  const pad = variant === "inline" ? "p-4" : "p-5 sm:p-6";

  if (loading) {
    return (
      <div className={`border border-[#D6D3CD] bg-[#F5F3ED] ${pad} rounded-xl`} data-testid="pricing-panel-loading">
        <div className="flex items-center gap-2 text-[#57534E]">
          <Spinner size={18} className="animate-spin" />
          <span className="text-sm font-medium">Analyzing comparable sales…</span>
        </div>
      </div>
    );
  }

  const conf = CONF[rec?.confidence] || CONF.Low;

  return (
    <div className={`border border-[#0D1B2A] bg-white ${pad} rounded-xl shadow-sm`} data-testid="pricing-panel">
      {/* Header */}
      <div className="flex items-center gap-2 mb-1">
        <ChartLineUp size={variant === "inline" ? 18 : 20} weight="fill" className="text-[#B47A3C]" />
        <h3 className={`font-bold tracking-tight ${variant === "inline" ? "text-base" : "text-lg"}`}>
          Decision-support pricing
        </h3>
      </div>
      <p className="text-xs text-[#8A867D] mb-4">
        AI-assisted recommendation from recent comparable sales. Guidance, not an appraisal.
      </p>

      {/* Recommendation */}
      {rec?.available ? (
        <div className="rounded-xl border border-[#E7E3DA] bg-[#FBFAF6] p-4 mb-4" data-testid="pricing-recommendation">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div>
              <span className="text-xs font-semibold uppercase tracking-wide text-[#8A867D]">{strat.title} price</span>
              <div className="text-4xl font-extrabold tracking-tight text-[#0D1B2A]" data-testid="pricing-target">
                {money(target)}
              </div>
              <p className="text-sm text-[#57534E] mt-0.5">{strat.sub}</p>
            </div>
            <span className={`shrink-0 inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1 rounded-full border ${conf.cls}`} data-testid="pricing-confidence">
              <ShieldCheck size={13} weight="fill" /> {conf.label}
            </span>
          </div>

          {/* Strategy slider */}
          <div className="mt-4">
            <Slider value={[pos]} onValueChange={(v) => setPos(v[0])} min={0} max={100} step={1}
              data-testid="pricing-strategy-slider" aria-label="Pricing strategy" />
            <div className="flex justify-between mt-2 text-[11px] font-semibold text-[#8A867D]">
              <span>Quick sale</span><span>Balanced</span><span>Max return</span>
            </div>
          </div>

          <div className="flex items-center justify-between gap-3 mt-3 flex-wrap">
            <p className="text-xs text-[#8A867D]">
              Evidence band <strong className="text-[#0D1B2A]">{money(rec.low)}–{money(rec.high)}</strong>
              {sold?.avg_days_to_sale != null ? <> · typically sells in ~{sold.avg_days_to_sale} days</> : null}
            </p>
            {onUse ? (
              <button type="button" onClick={() => onUse(String(Math.round(target)))}
                data-testid="pricing-use-button"
                className="inline-flex items-center gap-1.5 h-9 px-4 bg-[#0D1B2A] text-[#FDFBF7] rounded-md text-sm font-semibold hover:bg-black transition-transform active:scale-95">
                <ChartLineUp size={14} weight="fill" /> Use {money(target)}
              </button>
            ) : null}
          </div>

          <p className="text-xs text-[#8A867D] mt-3 leading-snug">{rec.basis} {rec.confidence_reason}</p>

          {/* AI rationale (lazy) */}
          {rationale ? (
            <div className="mt-3 rounded-lg border border-[#E7E3DA] bg-white p-3" data-testid="pricing-rationale">
              <div className="flex items-center gap-1.5 mb-1 text-[#B47A3C]">
                <Sparkle size={13} weight="fill" />
                <span className="text-[11px] font-bold uppercase tracking-wide">Why this range</span>
              </div>
              <p className="text-sm text-[#33302B] leading-relaxed">{rationale}</p>
            </div>
          ) : (
            <button type="button" onClick={explain} disabled={explaining}
              data-testid="pricing-explain-button"
              className="mt-3 inline-flex items-center gap-1.5 text-sm font-semibold text-[#B47A3C] hover:text-[#6B4420] disabled:opacity-60">
              {explaining ? <><Spinner size={14} className="animate-spin" /> Thinking…</>
                : <><Sparkle size={14} weight="fill" /> Why this range?</>}
            </button>
          )}
        </div>
      ) : (
        <div className="rounded-xl border border-dashed border-[#D6D3CD] bg-[#FBFAF6] p-4 mb-4" data-testid="pricing-recommendation-empty">
          <p className="text-sm text-[#57534E]">
            Not enough comparable retail sales yet to recommend a range for this piece.
            As your community sold pool grows, this sharpens — we never fabricate a number.
          </p>
        </div>
      )}

      {/* Two-column evidence: recent sold + current market */}
      <div className="grid sm:grid-cols-2 gap-3">
        <div className="rounded-lg border border-[#E7E3DA] bg-white p-3" data-testid="pricing-sold-comps">
          <div className="flex items-center gap-1.5 mb-1.5">
            <ChartLineUp size={14} weight="fill" className="text-[#166534]" />
            <span className="text-[11px] font-bold uppercase tracking-wide text-[#57534E]">Recent retail sold</span>
          </div>
          {sold?.available ? (
            <>
              <p className="text-sm text-[#0D1B2A]">
                Median <strong>{money(sold.median_sold)}</strong>
                <span className="text-[#8A867D]"> ({money(sold.low)}–{money(sold.high)})</span>
              </p>
              <p className="text-xs text-[#8A867D] mt-1">
                {sold.sample_size} {sold.sample_size === 1 ? "sale" : "sales"}
                {sold.scope === "community" ? " across categories" : ` · ${sold.label}`}
                {sold.recent_12mo ? ` · ${sold.recent_12mo} in last 12 mo` : ""}
              </p>
            </>
          ) : (
            <p className="text-xs text-[#8A867D]">Community sold pool is still building for this piece.</p>
          )}
        </div>

        <div className="rounded-lg border border-[#E7E3DA] bg-white p-3" data-testid="pricing-active-market">
          <div className="flex items-center gap-1.5 mb-1.5">
            <Storefront size={14} weight="fill" className="text-[#B47A3C]" />
            <span className="text-[11px] font-bold uppercase tracking-wide text-[#57534E]">Active listings</span>
            <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-[#F1EFE9] text-[#8A867D]">Coming soon</span>
          </div>
          <p className="text-xs text-[#8A867D] leading-snug">{data?.active_market?.note}</p>
        </div>
      </div>

      {/* Sources + hard-rule note */}
      <div className="mt-3">
        <button type="button" onClick={() => setShowSources((s) => !s)}
          className="inline-flex items-center gap-1 text-[11px] font-semibold text-[#8A867D] hover:text-[#57534E]"
          data-testid="pricing-sources-toggle">
          {showSources ? <CaretUp size={12} /> : <CaretDown size={12} />} Sources & method
        </button>
        {showSources ? (
          <div className="mt-2 rounded-lg bg-[#F5F3ED] p-3" data-testid="pricing-sources">
            <ul className="space-y-1">
              {(data?.sources || []).map((s, i) => (
                <li key={i} className="text-xs text-[#57534E] flex gap-1.5">
                  <span className="font-semibold text-[#0D1B2A]">{s.label}:</span>
                  <span>{s.detail}</span>
                </li>
              ))}
            </ul>
            <p className="text-[11px] text-[#8A867D] mt-2 flex items-start gap-1.5">
              <Info size={13} className="mt-0.5 shrink-0" />
              Retail sold prices only. We never blend auction or wholesale prices into a recommendation, and every figure is anonymized &amp; aggregated.
            </p>
          </div>
        ) : null}
      </div>
    </div>
  );
}
